import React from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';

const ProgressSection = ({
  currentProject,
  results = [],
  progress = 0
}) => {
  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const totalCount = results.length;
  
  const isCompleted = progress >= 100;
  const hasErrors = errorCount > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            {isCompleted ? 'Mobilization Complete' : 'Mobilization in Progress'}
          </h3>
          <p className="text-sm text-gray-600">
            {isCompleted 
              ? `Processed ${totalCount} projects`
              : currentProject || 'Preparing...'
            }
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {!isCompleted && (
            <CgSpinner className="w-6 h-6 text-blue-500 animate-spin" />
          )}
          
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-800">
              {progress}%
            </div>
            <div className="text-sm text-gray-600">
              {successCount + errorCount} / {totalCount || '?'}
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Overall Progress</span>
          <span className="text-sm text-gray-600">{progress}%</span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ease-out ${
              isCompleted 
                ? hasErrors 
                  ? 'bg-yellow-500' 
                  : 'bg-green-500'
                : 'bg-blue-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      
      {/* Statistics */}
      {totalCount > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{successCount}</div>
            <div className="text-sm text-green-700">Successful</div>
          </div>
          
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{errorCount}</div>
            <div className="text-sm text-red-700">Failed</div>
          </div>
          
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-600">{totalCount}</div>
            <div className="text-sm text-gray-700">Total</div>
          </div>
        </div>
      )}
      
      {/* Results List */}
      {results.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-800 mb-3">Project Results:</h4>
          
          <div className="max-h-60 overflow-y-auto space-y-2">
            {results.map((result, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  result.status === 'success'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {result.status === 'success' ? (
                    <FaCheckCircle className="text-green-600" />
                  ) : (
                    <FaExclamationTriangle className="text-red-600" />
                  )}
                  
                  <div>
                    <div className="font-medium text-gray-800">
                      {result.project}
                    </div>
                    {result.status === 'success' && result.count && (
                      <div className="text-sm text-gray-600">
                        {result.count} employees mobilized
                      </div>
                    )}
                    {result.status === 'error' && result.error && (
                      <div className="text-sm text-red-600">
                        {result.error}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  result.status === 'success'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {result.status === 'success' ? 'Success' : 'Failed'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Current Status */}
      {!isCompleted && currentProject && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CgSpinner className="w-4 h-4 text-blue-500 animate-spin" />
            <span className="text-blue-800 font-medium">
              Currently processing: {currentProject}
            </span>
          </div>
        </div>
      )}
      
      {/* Completion Message */}
      {isCompleted && (
        <div className={`mt-4 p-4 rounded-lg border ${
          hasErrors
            ? 'bg-yellow-50 border-yellow-200'
            : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center space-x-2 mb-2">
            {hasErrors ? (
              <FaExclamationTriangle className="text-yellow-600" />
            ) : (
              <FaCheckCircle className="text-green-600" />
            )}
            <span className={`font-medium ${
              hasErrors ? 'text-yellow-800' : 'text-green-800'
            }`}>
              {hasErrors 
                ? 'Mobilization completed with some errors'
                : 'All projects mobilized successfully!'
              }
            </span>
          </div>
          
          <p className={`text-sm ${
            hasErrors ? 'text-yellow-700' : 'text-green-700'
          }`}>
            {successCount} out of {totalCount} projects were processed successfully.
            {hasErrors && ' Please review the failed projects above.'}
          </p>
          
          {!hasErrors && (
            <p className="text-sm text-green-700 mt-2">
              This dialog will close automatically in a few seconds.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressSection;